import { Address } from '@interfaces';
import { TransactionTypesEnum, YesNoEmpty } from '@interfaces/models';
import { InvestmentObjectives } from '@interfaces/models/investment-objectives-types';
import {
  EmploymentStatus,
  VgEmploymentState,
} from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { MFEAddress } from '@interfaces/models/open-account-mfes/mfe-common.interfaces';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { Action } from '@ngrx/store';
import { PhoneValidationStatus } from '@shared/models/phone-number.interface';
import {
  HydrationAddress,
  HydrationFile,
  HydrationPrimaryOwner,
  HydrationResult,
  HydrationSelectedAccounts,
} from '@shared/services/hydration/hydration.models';
import { stripUniqueFileNameSuffix } from '@src/app/secure/document-upload/helpers';
import { UpdateOtherBene } from '@store/actions/beneficiaries/beneficiaries.actions';
import { DecedentInfoUpdate } from '@store/actions/decedent-info/decedent-info.actions';
import {
  updateFilesMetaData,
  updateSelectedAccountTypes,
} from '@store/actions/documents/documents.actions';
import { EstateUpdate } from '@store/actions/estate/estate.actions';
import { UpdateIncomeNetWorth } from '@store/actions/income-net-worth/income-net-worth.actions';
import { UpdateInvestmentObjectives } from '@store/actions/investment-objectives/investment-objectives.action';
import {
  updateLetterFilesMetaData,
  updateVgHasLetter,
} from '@store/actions/letter-upload/letter-upload.actions';
import { UpdateMfePersonalInfoClientData } from '@store/actions/mfe-personal-info-client/mfe-personal-info-client.actions';
import {
  updateEmploymentMfeData,
  updateSettlementFundChoice,
} from '@store/actions/open-account/open-account.actions';
import { UpdateMfePersonalAddress } from '@store/actions/personal-info-address/personal-info-address.actions';
import { UpdateMfePhoneFromGetData } from '@store/actions/personal-phone/personal-phone.actions';
import {
  UpdateSelectedAccountsFrom,
  UpdateSelectedAccountTo,
  UpdateTransactionType,
} from '@store/actions/selected-client-accounts/selected-client-accounts.actions';
import {
  restoreTaxCheckboxSelections,
  restoreTaxFilesMetaData,
} from '@store/actions/tax-document-upload/tax-document-upload.actions';
import { UpdateTriageSelections } from '@store/actions/triage/triage.actions';
import {
  TCFTrustInfoUpdate,
  TrustTaxUpdate,
  TrustUpdate,
} from '@store/actions/trust/trust.actions';
import { TransactionState } from '@store/reducers';
import { IFileMetaData } from '@store/reducers/document-upload/document-upload.reducer';
import { initialPersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import {
  initialState as selectedClientAccountsInitialState,
  ISelectedAccount,
  ISelectedClientAccountsState,
} from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';

/**
 * Builds NgRx actions for slices that live outside of the transaction-scoped state and are
 * shared across the entire transfer (decedent info, primary owner personal info, income/net worth,
 * employment MFE data, address, phone, and document upload metadata).
 *
 * Emits an action only when the corresponding section is present on the HydrationResult.
 * Returns [] when nothing hydratable is present.
 */
export function buildGlobalActions(result: HydrationResult): Action[] {
  const actions: Action[] = [];

  if (result.decedentInfo) {
    actions.push(DecedentInfoUpdate({ payload: { ...result.decedentInfo } }));
  }

  const primaryOwner = result.primaryOwner;

  if (primaryOwner) {
    const personalInfoAction = buildPersonalInfoAction(primaryOwner);

    if (personalInfoAction) {
      actions.push(personalInfoAction);
    }

    if (primaryOwner.annualIncome !== undefined || primaryOwner.liquidNetWorth !== undefined) {
      actions.push(
        UpdateIncomeNetWorth({
          payload: {
            annualIncome: primaryOwner.annualIncome ?? '',
            liquidNetWorth: primaryOwner.liquidNetWorth ?? '',
          },
        }),
      );
    }

    if (primaryOwner.employment) {
      actions.push(
        updateEmploymentMfeData({
          ownerIndex: 0,
          employment: mapEmployment(primaryOwner.employment),
        }),
      );
    }

    if (primaryOwner.phoneNumber) {
      actions.push(
        UpdateMfePhoneFromGetData({
          payload: {
            phoneNumber: primaryOwner.phoneNumber,
            isMobile: primaryOwner.isMobile ?? false,
            validationStatus: PhoneValidationStatus.VALID,
          },
        }),
      );
    }

    const addressAction = buildAddressAction(primaryOwner);

    if (addressAction) {
      actions.push(addressAction);
    }
  }

  const fileBuckets = [
    { files: result.documentFiles, action: updateFilesMetaData },
    { files: result.letterFiles, action: updateLetterFilesMetaData },
    // Restore, not update: updateTaxFilesMetaData marks isL8ITWUploadPageVisited, which
    // made resuming a draft skip the L8/ITW page the restored files belong to.
    { files: result.taxFiles, action: restoreTaxFilesMetaData },
  ];

  for (const { files, action } of fileBuckets) {
    if (!files?.length) {
      continue;
    }

    const payload = mapDocumentFiles(files);

    if (payload.length > 0) {
      actions.push(action({ payload }));
    }
  }

  if (result.declaredAccountTypes && result.declaredAccountTypes.length > 0) {
    actions.push(updateSelectedAccountTypes({ payload: [...result.declaredAccountTypes] }));
  }

  if (result.taxCheckboxSelections) {
    actions.push(restoreTaxCheckboxSelections({ payload: { ...result.taxCheckboxSelections } }));
  }

  if (result.vanguardHasLetter !== undefined) {
    actions.push(updateVgHasLetter({ payload: result.vanguardHasLetter }));
  }

  return actions;
}

/**
 * Builds one TransactionState from a HydrationResult (one draft → one transaction entity).
 *
 * Required fields sourced from result:
 *  - accountTriage: from result.triage (subset of TriageState overlapping AccountTriageState)
 *  - selectedClientAccounts: from result.selectedAccs (mapped shape)
 *  - beneficiaries: from result.beneficiaries (Person[] passthrough)
 *  - currentTransactionId: from the argument
 *  - currentTransactionEditMode: false
 *  - firstNewAccount: false — assigned across the whole set by
 *    markFirstNewAccountTransaction, which needs to see every sibling
 *  - settlementFund: 'FED'/'VCD' → enum; anything else (incl. 'VCP') → null
 *
 * Optional fields (estate, trust, investmentObjectives) are left undefined when absent.
 */
export function buildTransactionState(
  result: HydrationResult,
  currentTransactionId: string,
  draftLink?: { draftId: string; draftSequence: number },
): TransactionState {
  const transactionState: TransactionState = {
    currentTransactionId,
    currentTransactionEditMode: false,
    firstNewAccount: false,
    accountTriage: mapTriageToAccountTriage(result),
    beneficiaries: result.beneficiaries ? [...result.beneficiaries] : [],
    selectedClientAccounts: mapSelectedAccounts(
      result.selectedAccs,
      result.triage?.iraType,
      result.triage?.iraOption,
    ),
    settlementFund: mapSettlementFund(result.settlementFund),
  };

  if (result.estate) {
    transactionState.estate = { ...result.estate } as TransactionState['estate'];
  }
  if (result.trust) {
    transactionState.trust = { ...result.trust } as TransactionState['trust'];
  }
  if (result.trustTaxInfo) {
    transactionState.trustTaxInfo = {
      ...result.trustTaxInfo,
    } as TransactionState['trustTaxInfo'];
  }
  if (result.investmentObjectives) {
    transactionState.investmentObjectives = mapInvestmentObjectives(result.investmentObjectives);
  }

  if (draftLink) {
    transactionState.draftId = draftLink.draftId;
    transactionState.draftSequence = draftLink.draftSequence;
  }

  return transactionState;
}

/**
 * Flags the first new-account transaction, mirroring what a live session produces.
 *
 * The live flow marks exactly one new-account transaction as `firstNewAccount` — the one
 * that owns the OA profile shared by every new account in the transfer. The KU4 draft has
 * no field for it, but it does not need one: siblings are hydrated in draft-sequence (i.e.
 * creation) order, so the transaction that held the flag is always the first NEW_ACCOUNT one.
 *
 * Without this, a resumed draft has the flag on no transaction at all, and the repair in
 * `accountTriageContinue$` then sets it on the first new-account transaction while the
 * current transaction is the last sibling — leaving the flag and the cursor disagreeing,
 * which misroutes edit-from-review.
 */
export function markFirstNewAccountTransaction(states: TransactionState[]): TransactionState[] {
  const firstIndex = states.findIndex(
    (state) => state.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
  );

  if (firstIndex === -1) {
    return states;
  }

  return states.map((state, index) =>
    index === firstIndex ? { ...state, firstNewAccount: true } : state,
  );
}

/**
 * Builds NgRx actions for non-transaction slices used by single/trust (non-multi) flows:
 * triage, estate, trust, beneficiaries, and the two selected-accounts setters.
 *
 * Emits an action only when the corresponding section is present. Returns [] when nothing present.
 */
export function buildFlowActions(result: HydrationResult): Action[] {
  const actions: Action[] = [];

  if (result.triage) {
    actions.push(
      UpdateTriageSelections({
        payload: { ...result.triage } as Parameters<typeof UpdateTriageSelections>[0]['payload'],
      }),
    );
  }

  if (result.estate) {
    actions.push(
      EstateUpdate({
        payload: { ...result.estate } as Parameters<typeof EstateUpdate>[0]['payload'],
      }),
    );
  }

  if (result.trust) {
    actions.push(
      TrustUpdate({
        payload: { ...result.trust } as Parameters<typeof TrustUpdate>[0]['payload'],
      }),
    );
    actions.push(
      TCFTrustInfoUpdate({
        payload: {
          trustName: result.trust.trustName ?? '',
          trustDate: result.trust.trustDate ?? '',
          trusteeInformation: [],
        },
      }),
    );
  }

  if (result.trustTaxInfo) {
    actions.push(
      TrustTaxUpdate({
        payload: { ...result.trustTaxInfo } as Parameters<typeof TrustTaxUpdate>[0]['payload'],
      }),
    );
  }

  if (result.beneficiaries) {
    actions.push(
      UpdateOtherBene({
        payload: [...result.beneficiaries] as Parameters<typeof UpdateOtherBene>[0]['payload'],
      }),
    );
  }

  const selectedAccs = result.selectedAccs;

  if (selectedAccs) {
    const fromList = selectedAccs.transferFrom?.selectedAccounts;

    if (fromList && fromList.length > 0) {
      actions.push(
        UpdateSelectedAccountsFrom({
          payload: fromList.map(toSelectedAccount),
        }),
      );
    }

    const to = selectedAccs.transferTo?.selectedAccount;

    if (to) {
      actions.push(UpdateSelectedAccountTo({ payload: toSelectedAccount(to) }));

      // Routing selectors branch on transactionType; left UNKNOWN it sends a resumed draft
      // to terms/new regardless of the real destination.
      actions.push(
        UpdateTransactionType({
          payload: to.accountData?.accountId
            ? TransactionTypesEnum.EXISTING
            : TransactionTypesEnum.NEW_ACCOUNT,
        }),
      );
    }
  }

  const settlementFund = mapSettlementFund(result.settlementFund);

  if (settlementFund) {
    actions.push(updateSettlementFundChoice({ payload: settlementFund }));
  }

  // buildTransactionState covers the multi branch; the non-multi branch needs the dispatch.
  if (result.investmentObjectives) {
    actions.push(
      UpdateInvestmentObjectives({
        objectives: mapInvestmentObjectives(result.investmentObjectives),
      }),
    );
  }

  return actions;
}

// -------------------- helpers --------------------

function buildPersonalInfoAction(owner: HydrationPrimaryOwner): Action | null {
  const hasName =
    !!owner.name && (!!owner.name.firstName || !!owner.name.middleName || !!owner.name.lastName);
  const hasWa1Extras =
    !!owner.email ||
    !!owner.ssn ||
    !!owner.gender ||
    !!owner.countryOfCitizenship ||
    !!owner.suffix;

  if (!hasName && !owner.dateOfBirth && !owner.citizenshipStatus && !hasWa1Extras) {
    return null;
  }

  return UpdateMfePersonalInfoClientData({
    payload: {
      isInformationCorrect: null,
      personalInfo: {
        affiliationStartDate: '',
        firstName: owner.name?.firstName ?? '',
        middleName: owner.name?.middleName ?? '',
        lastName: owner.name?.lastName ?? '',
        email: owner.email ?? '',
        birthDate: owner.dateOfBirth ?? '',
        citizenshipStatus: owner.citizenshipStatus ?? '',
        gender: owner.gender ?? '',
        countryOfCitizenship: owner.countryOfCitizenship ?? '',
        suffix: owner.suffix ?? '',
        ...(owner.ssn !== undefined ? { ssn: owner.ssn } : {}),
      },
    },
  });
}

/**
 * Mirrors the prospect path in personal-data.effects. `streetAddressDropdown` stays empty
 * because it is a positional index into a list the draft does not store — a restored index
 * would point at an arbitrary address.
 *
 * `validationStatus` is left empty so the MFE revalidates on resume.
 */
function buildAddressAction(owner: HydrationPrimaryOwner): Action | null {
  // Either address alone stands in for both — the MFE always needs a mailing and a
  // street address, and a draft that saved only one has nothing else to offer.
  const residence = owner.primaryResidence ?? owner.primaryMailing;
  const mailing = owner.primaryMailing ?? owner.primaryResidence;

  if (!residence || !mailing) {
    return null;
  }

  const streetAddress = withAddressLines(mapHydrationAddress(residence));
  const mailingAddress = withAddressLines(mapHydrationAddress(mailing));

  return UpdateMfePersonalAddress({
    payload: {
      ...initialPersonalAddressState,
      streetAddress,
      mailingAddress,
      isStreetAddressSame: isSameAddress(streetAddress, mailingAddress),
      isClientAddressesLoaded: true,
    },
  });
}

function isSameAddress(left: MFEAddress, right: MFEAddress): boolean {
  return (
    left.street === right.street &&
    left.city === right.city &&
    left.state === right.state &&
    left.zipCode === right.zipCode
  );
}

function mapTriageToAccountTriage(result: HydrationResult): TransactionState['accountTriage'] {
  if (!result.triage) {
    return {} as TransactionState['accountTriage'];
  }

  // The following fields belong to the GLOBAL TriageState only. They must NOT
  // leak into the per-transaction AccountTriageState (which does not declare
  // them), otherwise transaction entities get polluted with global routing
  // answers (trust-to-trust flow flags, multi-availability, decedent-marital).
  const {
    flowType: _flowType,
    isSingleExecutor: _isSingleExecutor,
    isUpdatingTrust: _isUpdatingTrust,
    hasPassedAway: _hasPassedAway,
    isSuccessorOrLivingTrustee: _isSuccessorOrLivingTrustee,
    isInheritingMultipleAccounts: _isInheritingMultipleAccounts,
    isTrusteeOrEstate: _isTrusteeOrEstate,
    isDecedentMarried,
    isTrusteeOrg: _isTrusteeOrg,
    isSingleTrustee: _isSingleTrustee,
    ...perTransaction
  } = result.triage;

  const accountTriage = {
    ...perTransaction,
    wasMarried: isDecedentMarried,
  } as unknown as TransactionState['accountTriage'];

  // No wire slot for isDecedentSpouse, and wasMarried=YES with it unanswered is a dead
  // end, so default to YES when married. Check undefined, not falsy — EMPTY ('') is a real
  // "not yet answered" state distinct from undefined, and NO is a real saved answer —
  // neither must be coerced to YES.
  if (isDecedentMarried === YesNoEmpty.YES && accountTriage.isDecedentSpouse === undefined) {
    accountTriage.isDecedentSpouse = YesNoEmpty.YES;
  }

  return accountTriage;
}

function mapEmployment(
  employment: NonNullable<HydrationPrimaryOwner['employment']>,
): Partial<VgEmploymentState> {
  const spouse = employment.marriedToVanguardEmployee === true;
  const associated = employment.finraAffiliated === true;
  const control = employment.controlPerson === true;
  const controlCompanies = employment.controlCompanies ?? [];

  const employmentStatus: VgEmploymentState['employmentStatus'] = {
    employmentStatus: (employment.employmentStatus as EmploymentStatus) ?? EmploymentStatus.UNKNOWN,
    employer: employment.employer ?? '',
    occupation: employment.occupation ?? '',
    ...(employment.employerAddress
      ? { streetAddress: mapHydrationAddress(employment.employerAddress) }
      : {}),
  };

  return {
    employmentStatus,
    associations: {
      spouse,
      associated,
      control,
      none: !(spouse || associated || control),
      controlCompanyName: controlCompanies.map((c) => c.companyName ?? ''),
      controlCompanyTicker: controlCompanies.map((c) => c.companyTicker ?? ''),
    },
  };
}

function mapHydrationAddress(address: HydrationAddress): MFEAddress {
  return {
    street: address.street ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zipCode: address.zip ?? '',
  };
}

/** How many slots the address MFE emits in addressLines. */
const MFE_ADDRESS_LINE_COUNT = 4;

/**
 * `secure/submit` 400s on an address with no addressLines. The MFE normally writes them when
 * the client picks from its dropdown, which a resumed flow never reaches. Client address only
 * — that is what submit validates; the employer address keeps the plain MFEAddress shape.
 */
function withAddressLines(address: MFEAddress): Address {
  return {
    ...address,
    addressLines: [address.street, ...Array<string>(MFE_ADDRESS_LINE_COUNT - 1).fill('')],
  };
}

function mapDocumentFiles(files: HydrationFile[]): IFileMetaData[] {
  return files.reduce<IFileMetaData[]>((acc, file) => {
    // The draft stores the unique S3 key. Keep it so the page's existing S3-restore branch
    // can fetch the bytes back, and show the user the name without the uuid.
    if (file.filename) {
      acc.push({
        fileName: stripUniqueFileNameSuffix(file.filename),
        objectURL: '',
        s3BucketFileName: file.filename,
      });
    }

    return acc;
  }, []);
}

function mapSelectedAccounts(
  selectedAccs: HydrationSelectedAccounts | undefined,
  iraType?: string,
  iraOption?: string,
): ISelectedClientAccountsState {
  const state: ISelectedClientAccountsState = {
    ...selectedClientAccountsInitialState,
    transferFrom: { ...selectedClientAccountsInitialState.transferFrom },
    transferTo: { ...selectedClientAccountsInitialState.transferTo },
  };

  if (iraType !== undefined) {
    state.transferTo.iraType = iraType as ISelectedClientAccountsState['transferTo']['iraType'];
  }
  if (iraOption !== undefined) {
    state.transferTo.iraOption =
      iraOption as ISelectedClientAccountsState['transferTo']['iraOption'];
  }

  if (!selectedAccs) {
    return state;
  }

  const fromList = selectedAccs.transferFrom?.selectedAccounts ?? [];
  state.transferFrom = { ...state.transferFrom, selectedAccounts: fromList.map(toSelectedAccount) };

  const to = selectedAccs.transferTo?.selectedAccount;

  if (to) {
    state.transferTo = { ...state.transferTo, selectedAccount: toSelectedAccount(to) };
    // An existing receiving account carries an accountId (real Vanguard account);
    // a new account does not. The account-triage page reads transactionType to
    // decide the receiving-account UI, so derive it here.
    state.transactionType = to.accountData?.accountId
      ? TransactionTypesEnum.EXISTING
      : TransactionTypesEnum.NEW_ACCOUNT;
  }

  return state;
}

/** What `getSelectedAccountPayload` writes as `accountNumber` for a brand-new account. */
const NEW_ACCOUNT_NUMBER = 'newAccount';
/**
 * transfer-to reads `index !== '-1'` to mean "existing account", so a restored new account
 * MUST carry this or the flow routes to review/terms-existing instead of terms-new.
 */
const NEW_ACCOUNT_INDEX = '-1';

// `index` carries the accountId, not a list position: the eligible-account list can differ
// between saving and resuming, so a stored position would point at the wrong account.
function toSelectedAccount(input: {
  accountData?: { accountId?: string; accountName?: string; accountType?: string };
  accountNumber?: string;
}): ISelectedAccount {
  return {
    accountData: (input.accountData ?? {}) as ISelectedAccount['accountData'],
    accountNumber: input.accountNumber ?? '',
    index: resolveIndex(input),
    accountType: input.accountData?.accountType,
  };
}

function resolveIndex(input: {
  accountData?: { accountId?: string };
  accountNumber?: string;
}): string {
  if (input.accountData?.accountId) {
    return input.accountData.accountId;
  }

  // A new receiving account has no accountId; restore the sentinel, not the literal
  // 'newAccount', so the downstream pages recognise it.
  if (input.accountNumber === NEW_ACCOUNT_NUMBER) {
    return NEW_ACCOUNT_INDEX;
  }

  return input.accountNumber ?? '';
}

// The app's SettlementFundAoAEnum only supports FED and VCD. A draft may carry
// 'VCP' (VANGUARD_CASH_PLUS) from the library's dehydration, but there is no
// corresponding app enum value to hydrate it into, so it is intentionally
// dropped to null rather than mapped.
function mapSettlementFund(value: string | undefined): SettlementFundAoAEnum | null {
  if (value === SettlementFundAoAEnum.FED) {
    return SettlementFundAoAEnum.FED;
  }

  if (value === SettlementFundAoAEnum.VCD) {
    return SettlementFundAoAEnum.VCD;
  }

  return null;
}

function mapInvestmentObjectives(
  input: NonNullable<HydrationResult['investmentObjectives']>,
): InvestmentObjectives {
  return {
    CAP: input.CAP ?? null,
    Income: input.Income ?? null,
    Growth: input.Growth ?? null,
    Speculation: input.Speculation ?? null,
  };
}
